import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import FilterBar from '../settings/FilterBar';
import language from '../../variables/language';
import '../../css/irregular-verbs.css';

// Same "redder = lower, greener = higher" proficiency scale StudentWords.jsx
// uses for the ordinary words table - kept visually consistent across both
// places rather than inventing a second color language for the same number.
const PROFICIENCY_MAX = 8;
const proficiencyColor = (p) => {
   const clamped = Math.max(0, Math.min(PROFICIENCY_MAX, p || 0));
   const hue = (clamped / PROFICIENCY_MAX) * 120;
   return 'hsl(' + hue + ', 70%, 50%)';
}

// Fixed column order for the 3 canonical irregular-verb roles; any future,
// unexpected role (the backend's `role` column is free text, not an enum)
// still renders, just appended after these as its own column.
const ROLE_ORDER = ['base', 'past', 'participle'];

// studentId omitted -> catalog mode (admin dictionary view, no proficiency).
// studentId given -> student mode: words the student has no progress on at
// all render as a muted, dashed, proficiency-less chip.
export default function IrregularVerbsBoard({ studentId }) {
   const navigate = useNavigate();
   const lang = language[language.lang].irregularVerbsBoard;
   const langRoot = language[language.lang];
   const isStudentMode = !!studentId;

   const [groups, setGroups] = useState([]);
   const [proficiencyByWordId, setProficiencyByWordId] = useState(() => new Map());
   const [status, setStatus] = useState('loading');
   const [loadError, setLoadError] = useState(false);
   const [studentLoaded, setStudentLoaded] = useState(false);
   const [search, setSearch] = useState('');

   useEffect(() => {
      setStatus('loading');
      setLoadError(false);
      axios.get('/api/word-groups/irregular-verbs', { withCredentials: true })
         .then(response => setGroups(Array.isArray(response.data) ? response.data : []))
         .catch(err => {
            if(err.response && err.response.status === 401) navigate('/login');
            setGroups([]);
            setLoadError(true);
         })
         .finally(() => setStatus('done'));
   }, [navigate]);

   useEffect(() => {
      if(!isStudentMode) {
         setProficiencyByWordId(new Map());
         setStudentLoaded(true);
         return;
      }
      setStudentLoaded(false);
      axios.get('/api/wordstudents?student=' + studentId, { withCredentials: true })
         .then(response => {
            const map = new Map();
            (Array.isArray(response.data) ? response.data : []).forEach(ws => {
               if(ws.word) map.set(ws.word.id, ws.proficiency);
            });
            setProficiencyByWordId(map);
         })
         .catch(err => {
            if(err.response && err.response.status === 401) navigate('/login');
         })
         .finally(() => setStudentLoaded(true));
   }, [isStudentMode, studentId, navigate]);

   const roleLabel = (role) => {
      if(role === 'base') return lang.roleBase;
      if(role === 'past') return lang.rolePast;
      if(role === 'participle') return lang.roleParticiple;
      return role;
   };

   const roles = useMemo(() => {
      const extra = new Set();
      groups.forEach(g => (g.members || []).forEach(m => {
         if(!ROLE_ORDER.includes(m.role)) extra.add(m.role);
      }));
      return [...ROLE_ORDER, ...extra];
   }, [groups]);

   // student view: a verb row only appears when the student has at least one
   // of its forms in their words; catalog view shows every verb
   const visibleGroups = useMemo(() => {
      if(!isStudentMode) return groups;
      return groups.filter(g => (g.members || []).some(m => m.wordId != null && proficiencyByWordId.has(m.wordId)));
   }, [groups, isStudentMode, proficiencyByWordId]);

   const filteredGroups = useMemo(() => {
      const q = search.trim().toLowerCase();
      if(!q) return visibleGroups;
      return visibleGroups.filter(g => {
         if((g.label || '').toLowerCase().includes(q)) return true;
         return (g.members || []).some(m => (m.word || '').toLowerCase().includes(q));
      });
   }, [visibleGroups, search]);

   const renderChip = (member) => {
      // a form that is not in the dictionary yet arrives with no wordId - it
      // still shows in its slot, muted, so the verb reads as complete
      if(member.wordId == null) {
         return (
            <span key={member.word} className='irregular-verbs-chip is-missing' title={lang.missingLabel}>
               {member.word}
            </span>
         );
      }
      if(!isStudentMode) {
         const firstTranslation = member.translations && member.translations[0] ? member.translations[0].translation : '';
         return (
            <span key={member.wordId} className='irregular-verbs-chip' title={firstTranslation}>
               {member.word}
            </span>
         );
      }
      if(!proficiencyByWordId.has(member.wordId)) {
         return (
            <span key={member.wordId} className='irregular-verbs-chip is-missing' title={lang.missingLabel}>
               {member.word}
            </span>
         );
      }
      const p = proficiencyByWordId.get(member.wordId) || 0;
      const color = proficiencyColor(p);
      return (
         <span key={member.wordId} className='irregular-verbs-chip is-known' style={{ borderColor: color, color }}>
            {member.word}
            <i className='irregular-verbs-chip-proficiency'>{p}</i>
         </span>
      );
   };

   return (
      <div className='irregular-verbs-board-wrap'>
         <FilterBar search={{ value: search, onChange: setSearch, placeholder: lang.searchPlaceholder }} resultCount={filteredGroups.length} />
         {status === 'loading' || !studentLoaded ? (
            <div className='irregular-verbs-board-empty'>{langRoot['Loading ...']}</div>
         ) : ''}
         {status === 'done' && studentLoaded && loadError ? (
            <div className='irregular-verbs-board-empty irregular-verbs-board-error'>{lang.loadError}</div>
         ) : ''}
         {status === 'done' && studentLoaded && !loadError && filteredGroups.length === 0 ? (
            <div className='irregular-verbs-board-empty'>{lang.noGroups}</div>
         ) : ''}
         {status === 'done' && studentLoaded && filteredGroups.length > 0 ? (
            <div className='irregular-verbs-board' style={{ '--irregular-verbs-cols': roles.length }}>
               <div className='irregular-verbs-board-row irregular-verbs-board-head'>
                  <span className='irregular-verbs-verb-cell'></span>
                  {roles.map(role => (
                     <span key={role} className='irregular-verbs-role-head'>{roleLabel(role)}</span>
                  ))}
               </div>
               {filteredGroups.map(group => (
                  <div className='irregular-verbs-board-row' key={group.id}>
                     <span className='irregular-verbs-verb-cell'>{group.label}</span>
                     {roles.map(role => {
                        const members = (group.members || []).filter(m => m.role === role);
                        return (
                           <div key={role} className='irregular-verbs-role-cell' data-role-label={roleLabel(role)}>
                              {members.length > 0 ? members.map(renderChip) : <span className='irregular-verbs-role-empty'>—</span>}
                           </div>
                        );
                     })}
                  </div>
               ))}
            </div>
         ) : ''}
      </div>
   );
}
